import React, { useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
  alpha,
} from "@mui/material";
import EditNoteIcon from "@mui/icons-material/EditNote";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import HistoryIcon from "@mui/icons-material/History";
import ModeEditOutlineIcon from "@mui/icons-material/ModeEditOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useTranslation } from "react-i18next";
import InternalNoteContent from "./InternalNoteContent";
import { getInitials } from "../../../utils/utils";
import { getMessageDate, INTERNAL_NOTE_CLUSTER_DEFAULT } from "../timelineUtils";
import InternalNoteExpandedEditor from "../InternalNoteExpandedEditor";
import InternalNoteHistoryDialog from "./InternalNoteHistoryDialog";

const parseDisplayDate = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    const trimmed = value.trim();
    const mysqlRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
    if (mysqlRegex.test(trimmed)) {
      const [datePart, timePart] = trimmed.split(" ");
      const [year, month, day] = datePart.split("-");
      return `${day}/${month}/${year} ${timePart}`;
    }
  }
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("pt-BR");
};

const formatTimeOnly = (message) => {
  const d = getMessageDate(message);
  if (!d) return "";
  return d.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

/** Anotação com PDF: oculta texto duplicado do nome do arquivo. */
function noteTextIsDuplicateOfLinkedDocument(message) {
  const doc = message?.document;
  if (!doc || typeof doc !== "object") return false;
  const docUrl = doc.url || doc.link;
  if (!docUrl || !String(docUrl).trim()) return false;
  const docName = String(doc.name || doc.fileName || doc.filename || "").trim();
  if (!docName) return false;
  const textRaw = message?.text != null ? String(message.text).trim() : "";
  if (!textRaw) return false;
  const stripHtml = (s) =>
    s.replace(/<[^>]*>/g, "").replace(/&nbsp;/gi, " ").trim();
  const norm = (s) =>
    s.normalize("NFKC").replace(/\s+/g, " ").trim().toLowerCase();
  return norm(stripHtml(textRaw)) === norm(docName);
}

const InternalNoteMessage = ({
  message,
  displayName,
  isOwnNote = false,
  children,
  cluster = INTERNAL_NOTE_CLUSTER_DEFAULT,
  onEditNote,
  onDeleteNote,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("chat");
  const { isGrouped, isFirst, isLast, isOnly } = cluster;
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorValue, setEditorValue] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isContinuation = isGrouped && !isFirst;
  const showHeader = isFirst || isOnly;
  const showAuthorRow = isFirst || isOnly;
  const showFooter = isLast || isOnly;

  const senderName = displayName || message?.senderName || t("messages.teamFallback", "Equipe");
  const displayDate = parseDisplayDate(
    message?.createdAt ||
      message?.updatedAt ||
      message?.lastUpdate ||
      message?.timestamp
  );
  const timeOnly = formatTimeOnly(message);

  const noteTextTrimmed = message?.text != null ? String(message.text).trim() : "";
  const showNoteBody =
    noteTextTrimmed !== "" && !noteTextIsDuplicateOfLinkedDocument(message);
  const isDeleted = Boolean(message?.deletedAt);
  const hasHistory = Array.isArray(message?.editHistory) && message.editHistory.length > 0;
  const canEdit =
    isOwnNote && message?.canEditNote && !isDeleted && typeof onEditNote === "function";
  const canDelete =
    isOwnNote && message?.canDeleteNote && !isDeleted && typeof onDeleteNote === "function";

  const accentColor = isOwnNote ? theme.palette.primary.main : theme.palette.warning.main;
  const accentDark = isOwnNote ? theme.palette.primary.dark : theme.palette.warning.dark;
  const chipLabel = isOwnNote
    ? t("messages.ownNoteChip", "Sua anotação")
    : t("messages.teamNoteChip", "Anotação interna");
  const surfaceBg = isOwnNote
    ? alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.22 : 0.08)
    : theme.palette.messages?.notes || theme.palette.action.hover;

  const authorLabel = isOwnNote ? t("messages.ownMessageYou", "Você") : senderName;
  const authorNamesForStrip = [message?.senderName, message?.userName, displayName, senderName].filter(
    Boolean
  );

  const borderSides = isOwnNote
    ? { borderRightWidth: 4, borderLeftWidth: 1 }
    : { borderLeftWidth: 4, borderRightWidth: 1 };

  const historyDialog = (
    <InternalNoteHistoryDialog
      open={historyOpen}
      onClose={() => setHistoryOpen(false)}
      message={message}
      authorLabel={authorLabel}
    />
  );

  if (isDeleted) {
    return (
      <Box
        sx={{
          width: "100%",
          maxWidth: "85%",
          display: "flex",
          justifyContent: isOwnNote ? "flex-end" : "flex-start",
        }}
      >
        <Tooltip title={t("messages.noteDeletedHint", "Ver conteúdo excluído e histórico")}>
          <Box
            component="button"
            type="button"
            onClick={() => setHistoryOpen(true)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              maxWidth: "100%",
              px: 1.25,
              py: 0.5,
              font: "inherit",
              textAlign: "left",
              cursor: "pointer",
              borderRadius: "10px",
              border: `1px dashed ${alpha(theme.palette.text.primary, 0.22)}`,
              bgcolor: "transparent",
              color: theme.palette.text.disabled,
              transition: "background-color 0.15s ease, color 0.15s ease",
              "&:hover": {
                bgcolor: alpha(theme.palette.text.primary, 0.04),
                color: theme.palette.text.secondary,
              },
              "&:focus-visible": {
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: 2,
              },
            }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 15, flexShrink: 0 }} />
            <Typography
              variant="caption"
              sx={{ fontStyle: "italic", fontSize: "0.72rem", lineHeight: 1.3 }}
            >
              {t("messages.noteDeleted", "Excluído")}
            </Typography>
            {displayDate && (
              <Typography
                variant="caption"
                sx={{ fontSize: "0.68rem", opacity: 0.75, lineHeight: 1.3 }}
              >
                · {displayDate}
              </Typography>
            )}
          </Box>
        </Tooltip>
        {historyDialog}
      </Box>
    );
  }

  const noteActions =
    canEdit || canDelete ? (
      <Box
        className="internal-note-actions"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.25,
          opacity: 0,
          transition: "opacity 0.15s ease",
          "@media (hover: none)": { opacity: 1 },
        }}
      >
        {canEdit && (
          <Tooltip title={t("messages.editNote", "Editar anotação")}>
            <IconButton
              size="small"
              aria-label={t("messages.editNote", "Editar anotação")}
              onClick={() => {
                setEditorValue(message?.text || "");
                setEditorOpen(true);
              }}
              sx={{
                p: 0.25,
                color: "text.secondary",
                "&:hover": { color: accentDark, bgcolor: alpha(accentColor, 0.12) },
              }}
            >
              <ModeEditOutlineIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        )}
        {canDelete && (
          <Tooltip title={t("messages.deleteNote", "Excluir anotação")}>
            <IconButton
              size="small"
              aria-label={t("messages.deleteNote", "Excluir anotação")}
              onClick={() => setDeleteOpen(true)}
              sx={{
                p: 0.25,
                color: "text.secondary",
                "&:hover": {
                  color: theme.palette.error.main,
                  bgcolor: alpha(theme.palette.error.main, 0.12),
                },
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    ) : null;

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "85%",
      }}
    >
      <Box
        sx={{
          borderRadius: "10px",
          border: `1px solid ${accentColor}`,
          ...borderSides,
          bgcolor: surfaceBg,
          overflow: "hidden",
          "&:hover .internal-note-actions, &:focus-within .internal-note-actions": {
            opacity: 1,
          },
          boxShadow: isContinuation
            ? "none"
            : isOwnNote
              ? `0 1px 3px ${alpha(theme.palette.primary.main, 0.25)}`
              : "0 1px 3px rgba(0, 0, 0, 0.06)",
          ...(isContinuation && {
            borderTop: "none",
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            mt: "-1px",
          }),
          ...(isGrouped && !isLast && {
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          }),
        }}
      >
        {showHeader && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 1,
              borderBottom:
                showNoteBody || children || isGrouped
                  ? `1px solid ${theme.palette.divider}`
                  : "none",
            }}
          >
            <EditNoteIcon sx={{ fontSize: 18, color: accentDark }} />
            <Chip
              label={chipLabel}
              size="small"
              sx={{
                height: 22,
                fontSize: "0.7rem",
                fontWeight: 600,
                bgcolor: accentColor,
                color: isOwnNote
                  ? theme.palette.primary.contrastText
                  : theme.palette.warning.contrastText,
              }}
            />
            <Box sx={{ flex: 1 }} />
            {displayDate && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                {displayDate}
              </Typography>
            )}
            {noteActions}
          </Box>
        )}

        {isContinuation ? (
          <Box
            sx={{
              pr: 1.5,
              pl: 6.75,
              py: 0.85,
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.65)}`,
            }}
          >
            {(timeOnly || noteActions) && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: 0.5,
                  mb: showNoteBody || children ? 0.5 : 0,
                }}
              >
                {timeOnly && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.68rem", lineHeight: 1.2 }}
                  >
                    {timeOnly}
                  </Typography>
                )}
                {noteActions}
              </Box>
            )}
            {showNoteBody && (
              <InternalNoteContent
                text={message.text}
                authorLabel={authorLabel}
                authorNamesForStrip={authorNamesForStrip}
                previewFadeColor={surfaceBg}
              />
            )}
            {children}
          </Box>
        ) : (
          <Box sx={{ display: "flex", gap: 1.25, px: 1.5, py: 1.25 }}>
            {showAuthorRow && (
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  fontSize: "0.8rem",
                  bgcolor: accentDark,
                  flexShrink: 0,
                }}
              >
                {getInitials(isOwnNote ? t("messages.ownMessageYou", "Você") : senderName)}
              </Avatar>
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {showAuthorRow && (
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                  {authorLabel}
                </Typography>
              )}

              {showNoteBody && (
                <InternalNoteContent
                  text={message.text}
                  authorLabel={authorLabel}
                  authorNamesForStrip={authorNamesForStrip}
                  previewFadeColor={surfaceBg}
                />
              )}

              {children}
            </Box>
          </Box>
        )}

        {showFooter && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              pr: 1.5,
              pl: isContinuation || (isGrouped && !isOnly) ? 6.75 : 1.5,
              pb: 1,
              pt: 0.25,
              color: theme.palette.text.secondary,
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: 14 }} />
            <Typography variant="caption" sx={{ fontSize: "0.68rem" }}>
              {t("messages.internalVisibilityHint", "Visível apenas para a equipe")}
            </Typography>
            {(message?.editedAt || hasHistory) && (
              <Button
                size="small"
                color="inherit"
                startIcon={<HistoryIcon sx={{ fontSize: "13px !important" }} />}
                onClick={() => setHistoryOpen(true)}
                sx={{
                  ml: "auto",
                  p: 0,
                  minWidth: 0,
                  fontSize: "0.68rem",
                  fontWeight: 400,
                  lineHeight: 1.2,
                  textTransform: "none",
                  color: "text.secondary",
                  "& .MuiButton-startIcon": { mr: 0.4 },
                  "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
                }}
              >
                {t("messages.noteEdited", "Editado")}
              </Button>
            )}
          </Box>
        )}
      </Box>

      <InternalNoteExpandedEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        message={editorValue}
        setMessage={setEditorValue}
        onSend={() => onEditNote?.(message.id, editorValue)}
        showAttachButton={false}
        title={t("messages.editNote", "Editar anotação")}
        sendLabel={t("messages.saveNote", "Salvar alterações")}
      />
      {historyDialog}
      <Dialog open={deleteOpen} onClose={() => !deleting && setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("messages.deleteNoteTitle", "Excluir anotação?")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t(
              "messages.deleteNoteHint",
              "O balão continuará no histórico e o conteúdo excluído poderá ser consultado."
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>
            {t("messages.cancel", "Cancelar")}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleting}
            onClick={async () => {
              setDeleting(true);
              try {
                await onDeleteNote?.(message.id);
                setDeleteOpen(false);
              } catch (_) {
                // O container exibe o erro e o diálogo permanece aberto.
              } finally {
                setDeleting(false);
              }
            }}
          >
            {deleting ? t("messages.deletingNote", "Excluindo…") : t("messages.deleteNote", "Excluir")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InternalNoteMessage;
